import styled, { css } from 'styled-components'
import { getRemValue } from '../../utils/styling/getRemValue'
import { StyledDomElement } from '../../App'
import { Box } from '../UI/Box'

export const Background = styled.div<{
  backgroundUrl: string
  mobileBackgroundUrl?: string
  height: string
  mobileH: string
  width?: string
  top?: number
  backgroundPosition?: string
  wideScreenBackgroundSize?: string
  backgroundGradient?: string
}>`
  background-image: ${({ backgroundGradient }) =>
      backgroundGradient ??
      `radial-gradient(
    54.86% 54.7% at 61.16% 39.14%,
    #ffffff00 0%,
    #04111b 100%
  )`},
    url('${({ backgroundUrl }) => backgroundUrl}');
  background-size: cover;
  background-position: ${({ backgroundPosition }) =>
    backgroundPosition ?? 'center'};
  background-repeat: no-repeat;

  height: ${({ height }) => height};
  @media (max-width: 768px) {
    background-image: ${({ backgroundGradient }) =>
        backgroundGradient ??
        `radial-gradient(
    54.86% 54.7% at 61.16% 39.14%,
    #ffffff00 0%,
    #04111b 100%
  )`},
      url('${({ backgroundUrl, mobileBackgroundUrl }) =>
        mobileBackgroundUrl ?? backgroundUrl}');
    width: calc(100vw + 32px); // 16px padding on each side
    left: -16px;
    height: ${({ mobileH }) => mobileH};
  }

  position: absolute;
  top: -${({ top }) => top ?? 0}px;
  left: 0;
  right: 0;
  overflow: hidden;
  width: ${({ width }) => width ?? `100vw`};
  z-index: 1;

  @media (min-width: 1600px) {
    background-size: ${({ wideScreenBackgroundSize }) =>
      wideScreenBackgroundSize ?? 'contain'};

    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: -${({ top }) => top ?? 0}px;
      overflow: hidden;
      width: 100%;
      // max-width: calc((100vw - 1400px) / 2);
      max-width: calc((100vw - 1400px));
      height: 100%;
      z-index: 1;
      background: linear-gradient(90deg, #04111b 50%, rgba(4, 17, 27, 0) 100%);
    }
    ::after {
      content: '';
      position: absolute;
      right: 0;
      top: -${({ top }) => top ?? 0}px;
      overflow: hidden;
      width: 100%;
      // max-width: calc((100vw - 1400px) / 2);
      max-width: calc((100vw - 1400px));
      height: 100%;
      z-index: 1;
      background: linear-gradient(-90deg, #04111b 50%, rgba(4, 17, 27, 0) 100%);
    }
  }
`

export const Container = styled.div<{
  pb: string
  pt: string
  mobilePt: string
  mobilePb: string
  bgColor?: string
  isFullSize?: boolean
}>`
  width: 100%;
  ${({ pb }) => pb && `padding-bottom: ${pb}`};
  ${({ pt }) => pt && `padding-top: ${pt}`};
  ${({ bgColor }) => bgColor && `background-color: ${bgColor}`};
  ${({ isFullSize }) =>
    isFullSize &&
    css`
      overflow: hidden;
      margin-top: -140px;
      position: absolute;
      top: 0;
      @media screen and (min-width: 1600px) {
        margin-top: -204px;
      }
    `};
  .bottom-right-decoration {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 0;
  }
  @media screen and (max-width: 600px) {
    .bottom-right-decoration {
      position: absolute;
      right: -250px;
      z-index: 0;
      min-width: 250vw;
    }
  }
  @media (max-width: 769px) {
    .bottom-right-decoration.character {
      transform: scale(0.7);
      transform-origin: bottom right;
      bottom: -64px;
      left: unset;
      right: 0;
      min-width: unset;
    }
    ${({ mobilePb }) => mobilePb && `padding-bottom: ${mobilePb}`};
    ${({ mobilePt }) => mobilePt && `padding-top: ${mobilePt}`};
  }
`

export const RadialGlow = styled.div`
  position: absolute;
  right: -20vw;
  top: 0;

  width: 100vw;

  fill: radial-gradient(
    25.55% 47.75% at 38.79% 69.91%,
    #f9e159 33.53%,
    #1f8ef5 82.36%,
    #f9e159 100%
  );

  opacity: 0.5;
  z-index: 0;
  pointer-events: none;
  filter: blur(250px);
`

export const Subheader = styled.div`
  color: #fff;
  leading-trim: both;
  text-edge: cap;
  font-variant-numeric: ordinal;
  font-feature-settings: 'dlig' on, 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px; /* 140% */
  letter-spacing: -0.8px;

  .number {
    font-size: 36px;
  }

  @media screen and (max-width: ${({ theme }) =>
      theme.layout.compact.maxWidth}px) {
    font-size: 18px;
    line-height: 26px; /* 144.444% */
    letter-spacing: -0.72px;
  }
`

export const PoolsSection = styled.div<{ marginTop?: string }>`
  margin-bottom: 5rem;
  margin-top: ${(props) => props.marginTop};
`

export const ButtonWrapper = styled.div`
  display: flex;
  display: flex;
  justify-content: center;
`

export const LoadingText = styled.p`
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
  font-size: 2.25rem;
  line-height: 2.5rem;
  text-align: center;
  text-transform: uppercase;
`

export const PoolCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (min-width: 1280px) {
    display: grid;
    margin-bottom: 4rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }
`

export function hexToRgba(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

type HomePageSectionProps = {
  zIndex?: string
  ref?: any
} & StyledDomElement

export const HomePageSection = styled.div<HomePageSectionProps>`
  position: relative;
  z-index: ${(props) => props.zIndex};
`

export const HeroBox = styled(Box)<{
  overflow: string
  heroFlexDirection: string
  heroMobileFlexDirection: string
}>`
  position: relative;
  overflow: ${({ overflow }) => overflow};

  flex-direction: ${({ heroFlexDirection }) => heroFlexDirection};
  @media (max-width: 1200px) {
    flex-direction: ${({ heroMobileFlexDirection }) => heroMobileFlexDirection};
  }
`

type HowToInvestFullWidthSectionProps = {
  isDarkTheme: boolean
} & StyledDomElement

export const HowToInvestFullWidthSection = styled.div<HowToInvestFullWidthSectionProps>`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  position: relative;
  margin-top: -35px;
  padding-top: 217px;
  background: url('./upcoming-cover-labs.png');
  background-repeat: no-repeat;
  background-size: 140%, 100%;
  background-position: top, bottom;

  @media screen and (min-width: 1000px) {
    margin-top: -113px;
    padding-top: 450px;
    background-size: 60%, 100%;
  }
`

export const ForceFullWidth = styled.div<{ position?: string; top?: number }>`
  width: 100vw;
  position: ${({ position }) => position ?? 'relative'};
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  ${({ top }) => top && `top:${top};`}
`

export const Relative = styled.div`
  position: relative;
`

export const TitlesWrapper = styled.div`
  width: 100%;
  text-align: center;
  h2,
  h3,
  ${Subheader} {
    margin: auto;
  }
  gap: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (min-width: 1280px) {
    width: 819px;
  }
`

type TitleProps = {
  fontSize?: number | null
  textColor?: string
  noUnderscore?: boolean
  isActive?: boolean
  maxWidth?: number
} & StyledDomElement

export const Title = styled.h2<TitleProps>`
  color: #fff;
  font-variant-numeric: ordinal;
  font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
  font-family: 'Space Grotesk';
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 78px; /* 108.333% */
  letter-spacing: -2.88px;

  @media screen and (max-width: ${({ theme }) =>
      theme.layout.compact.maxWidth}px) {
    font-size: 46px;
    line-height: 46px; /* 100% */
    letter-spacing: -1.84px;
  }
`

export const ButtonsWrapper = styled(Box)`
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1000px) {
    width: 410px;
    gap: 0;
    flex-direction: row;
  }
`

export const SingleButtonWrapper = styled.div`
  display: block;
  width: 100%;

  @media (min-width: 1000px) {
    margin-top: 40px;
    width: unset;
  }
`

export const IntroVideo = styled.video`
  position: absolute;
  bottom: 160px;
  right: 0;
  width: 290px;
  height: 163.12px;
  border-radius: 8px;
  @media (max-width: 1000px) {
    position: relative;
    width: 100%;
    height: auto;
    margin-top: 200px;
    margin-bottom: -140px;
  }
`
